import {
  LOGOUT_USER,
  LOGIN_USER,
  UPDATE_USER_ONBOARDING,
  ADD_USER_TOKEN,
  SET_SESSION_EXPIRED,
  SET_REDIRECT_TO_NOVA_MISMATCH_PAGE,
} from "../constants";

import axios from "@/utils/axios";
import { login, logout, firebaseAuth, getCurrentUser } from "@/utils/firebase";
import { path } from "ramda";

// initial state
const state = {
  isLoggedIn: false,
  userToken: "",
  sessionHasExpired: false,
  redirectToNovaMismatchPage: false,
};

// getters
const getters = {};

// actions
const actions = {
  logout({ commit, dispatch }) {
    axios.clearBearerToken();
    localStorage.removeItem("user-token");
    localStorage.removeItem("user-id");
    logout();
    dispatch("admin/reset", {}, { root: true });
    dispatch("classes/reset", {}, { root: true });
    dispatch("invite/reset", {}, { root: true });
    dispatch("organization/reset", {}, { root: true });
    dispatch("teachers/reset", {}, { root: true });
    dispatch("students/reset", {}, { root: true });
    dispatch("user/reset", {}, { root: true });

    return commit(LOGOUT_USER);
  },
  sessionExpired({ dispatch }) {
    dispatch("logout");
    dispatch("setSessionExpired", true);
  },
  setSessionExpired({ commit }, sessionExpired = false) {
    return commit(SET_SESSION_EXPIRED, sessionExpired);
  },
  firebaseAuthenticate({ dispatch }, loginData) {
    axios.clearBearerToken();

    return axios
      .post("/v1/accounts/auth/", { ...loginData })
      .then((response) => {
        const token = response.data.token;

        if (response.data.user_token) {
          localStorage.setItem("payment-token", response.data.user_token);
        }

        if (response.data.refresh_token) {
          localStorage.setItem("refresh-token", response.data.refresh_token);
        }

        return login(token);
      })
      .then(() => {
        const user = firebaseAuth.currentUser;
        return Promise.all([
          user.getIdTokenResult(true).then(path(["claims", "user_id"])),
          user.getIdToken(true),
        ]);
      })
      .then(([userUid, userToken]) => {
        return dispatch("successLoginCallBack", {
          userUid,
          userToken,
        });
      });
  },
  ssoAuthenticateCallback({ dispatch }, {token}) {
    axios.clearBearerToken();
    return login(token)
      .then(() => {
        const user = firebaseAuth.currentUser;
        return Promise.all([
          user.getIdTokenResult(true).then(path(["claims", "user_id"])),
          user.getIdToken(true),
        ]);
      })
      .then(([userUid, userToken]) => {
        return dispatch("successLoginCallBack", {
          userUid,
          userToken,
        });
      });
  },
  isSSO({ dispatch }, {email}) {
    // encode email for query param
    email = encodeURIComponent(email);
    return axios.get(`/v2/accounts/is-sso/?email=${email}`)
      .then((response) => {
        return response.data;
      });
  },
  ssoAuthenticate({ dispatch }, loginData) {
    axios.clearBearerToken();

    return axios
      .post("/v2/accounts/sso/auth/", { ...loginData })
      .then((response) => {
        const authorization_url = response.data.authorization_url;
        return authorization_url;
      });
  },
  login({ commit }) {
    return commit(LOGIN_USER);
  },
  // finishOnboarding({ commit }, userOnboarding) {
  //   commit(UPDATE_USER_ONBOARDING, userOnboarding);
  // },
  async restoreAuthData({ commit, state, dispatch }) {
    if (!state.isLoggedIn) {
      const userToken = localStorage.getItem("user-token");
      const userId = localStorage.getItem("user-id");
      if (userToken && userId) {
        // const currentUser = await getCurrentUser();
        // get new token id in case old token expired
        // const newUserToken = await currentUser.getIdToken(true);
        commit(ADD_USER_TOKEN, userToken);
        axios.addBearerToken(userToken);
        try {
          await dispatch(
            "user/updateCurrentUserId",
            { userId },
            { root: true }
          );
          await dispatch("login");
          await dispatch("user/loadUserData", {}, { root: true });
          await dispatch(
            "organization/reLoadUserOrganizationsData",
            {},
            { root: true }
          );
          return true;
        } catch (error) {
          error;
        }
      }
    }

    await dispatch("logout");

    return false;
  },
  async successLoginCallBack({ dispatch, commit, rootGetters }, { userToken }) {
    axios.addBearerToken(userToken);
    let response;

    try {
      response = await axios.patch("/v1/accounts/sign_in", {});
    } catch {
      throw new Error("Please create an account");
    }

    const userUid = response.data.user_id;
    const newUserToken = await firebaseAuth.currentUser.getIdToken(true);

    axios.addBearerToken(newUserToken);
    localStorage.setItem("user-token", newUserToken);
    localStorage.setItem("user-id", userUid);

    await dispatch("user/updateCurrentUserId", { userId: userUid }, { root: true });
    await dispatch("organization/loadUserOrganizationsData", {}, { root: true });

    const userOrganizations = rootGetters['organization/userOrganizations'];
    const userHasClassicOrg = userOrganizations.some(org => !org.is_nova);
    const userHasOnlyNovaOrgs = userOrganizations.length > 0 && userOrganizations.every(org => org.is_nova);

    commit(SET_REDIRECT_TO_NOVA_MISMATCH_PAGE, false);

    if (userHasClassicOrg) {
      const firstClassicOrg = userOrganizations.find(org => !org.is_nova);
      if (firstClassicOrg) {
        await dispatch("organization/setCurrentOrganization", { organizationId: firstClassicOrg.id }, { root: true });
      }
      dispatch("user/showOnboarding", { show: true }, { root: true });
    } else if (userHasOnlyNovaOrgs) {
      commit(SET_REDIRECT_TO_NOVA_MISMATCH_PAGE, true);
      // Do NOT dispatch showOnboarding here
    } else {
      // No organizations or other cases
      dispatch("user/showOnboarding", { show: true }, { root: true });
    }

    commit(ADD_USER_TOKEN, newUserToken); // Commit token regardless of redirection path
    return newUserToken; // Return token or a status object if needed
  },
  async refreshToken({ dispatch, commit }) {
    let user = await getCurrentUser();
    const newUserToken = await user.getIdToken(true);
    localStorage.setItem("user-token", newUserToken);
    axios.addBearerToken(newUserToken);
    commit(ADD_USER_TOKEN, newUserToken);
    return newUserToken;
  },
  // facebookLogin({ dispatch }) {
  //   return facebookLogin()
  //     .then((user) => {
  //       return Promise.all([
  //         user.getIdTokenResult(true).then(path(["claims", "elsa_user_id"])),
  //         user.getIdToken(true),
  //       ]);
  //     })
  //     .then(([userUid, userToken]) => {
  //       return dispatch("successLoginCallBack", {
  //         userUid,
  //         userToken,
  //       });
  //     });
  // },
  // facebookSignIn({ commit }) {
  //   return facebookLogin()
  //     .then((user) => {
  //       return user.getIdToken(true);
  //     })
  //     .then((userToken) => {
  //       axios.addBearerToken(userToken);
  //       localStorage.setItem("user-token", userToken);
  //
  //       return commit(ADD_USER_TOKEN, userToken);
  //     });
  // },
  signUp({ dispatch }, userData) {
    const url = userData.is_invite
      ? "/v1/accounts/sign_in"
      : "/v1/checkouts/sign_up";
    delete userData.is_invite;
    return axios.post(url, userData);
  },
  setupOrganization({ dispatch }, payload) {
    return axios.post("/v1/checkouts/setup_organization", payload);
  },
};

// mutations
const mutations = {
  [LOGOUT_USER](state) {
    state.isLoggedIn = false;
    state.userToken = "";
  },
  [ADD_USER_TOKEN](state, token) {
    state.userToken = token;
  },
  [LOGIN_USER](state) {
    state.isLoggedIn = true;
  },
  [UPDATE_USER_ONBOARDING](state, userOnboarding) {
    state;
    userOnboarding;
  },
  [SET_SESSION_EXPIRED](state, sessionExpired) {
    state.sessionHasExpired = sessionExpired;
  },
  [SET_REDIRECT_TO_NOVA_MISMATCH_PAGE](state, value) {
    state.redirectToNovaMismatchPage = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
